/**
 * Farmer layer module.
 *
 * Renders farmer plot boundaries as polygons with household-type colors.
 */

import type { Map } from 'mapbox-gl';
import type { MapFarmer } from '../types/mapTypes';
import { farmerToFeature, featureCollection } from '../utils/geojson';

const SOURCE_ID = 'farmers-source';
const FILL_LAYER_ID = 'farmers-fill';
const LINE_LAYER_ID = 'farmers-line';

const HOUSEHOLD_COLORS: Record<string, string> = {
  standard: '#10B981',
  cooperative: '#3B82F6',
  vsla: '#8B5CF6',
  farmer_group: '#F59E0B',
};

function householdColor(type: string): string {
  return HOUSEHOLD_COLORS[type] || '#6B7280';
}

export function addFarmerLayers(map: Map): void {
  if (map.getSource(SOURCE_ID)) return;

  map.addSource(SOURCE_ID, {
    type: 'geojson',
    data: featureCollection([]),
  });

  map.addLayer({
    id: FILL_LAYER_ID,
    type: 'fill',
    source: SOURCE_ID,
    layout: {},
    paint: {
      'fill-color': ['get', '_fillColor'],
      'fill-opacity': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        0.6,
        0.3,
      ],
    },
    filter: ['==', ['get', '_type'], 'farmer'],
  });

  map.addLayer({
    id: LINE_LAYER_ID,
    type: 'line',
    source: SOURCE_ID,
    layout: {},
    paint: {
      'line-color': ['get', '_lineColor'],
      'line-width': 1.5,
      'line-dasharray': [4, 2],
    },
    filter: ['==', ['get', '_type'], 'farmer'],
  });
}

export function updateFarmerData(map: Map, farmers: MapFarmer[]): void {
  const source = map.getSource(SOURCE_ID);
  if (!source || source.type !== 'geojson') return;

  const features = farmers.map((farmer) => {
    const feature = farmerToFeature(farmer);
    const color = householdColor(farmer.householdType);
    (feature.properties as any)._fillColor = color;
    (feature.properties as any)._lineColor = color;
    return feature;
  });

  (source as any).setData(featureCollection(features));
}

export function removeFarmerLayers(map: Map): void {
  if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
  if (map.getLayer(FILL_LAYER_ID)) map.removeLayer(FILL_LAYER_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

export { SOURCE_ID as farmerSourceId };
